import React from "react";
import {
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  Paper,
  Typography,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { MemoryKV } from "../../core/memory/memory.types";
import { t } from "../../translations";

interface ProfilePreviewProps {
  latestEntry: MemoryKV | null;
  allEntries: MemoryKV;
  isExpanded: boolean;
  onToggleExpand: () => void;
  language: string;
}

export const ProfilePreview = ({
  latestEntry,
  allEntries,
  isExpanded,
  onToggleExpand,
  language,
}: ProfilePreviewProps) => {
  const [noteExpanded, setNoteExpanded] = React.useState(false);

  if (!latestEntry) {
    return null; // Don't render anything if there are no notes yet
  }

  const expandedHeight = isExpanded
    ? { xs: "60vh", sm: "50vh" }
    : {
        xs: noteExpanded ? "150px" : "100px",
        sm: noteExpanded ? "150px" : "100px",
      };
  const entries = Object.entries(allEntries);

  return (
    <Paper
      sx={{
        position: "relative",
        width: "100%",
        bgcolor: "white",
        height: expandedHeight,
        transition: "height 0.3s ease",
        boxShadow:
          "0 -4px 6px -1px rgb(0 0 0 / 0.1), 0 -2px 4px -2px rgb(0 0 0 / 0.1)",
      }}
    >
      {isExpanded ? (
        <div
          style={{
            padding: "16px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {t("profileDetails", language)}
            </Typography>
            <Button onClick={onToggleExpand} sx={{ color: "primary.main" }}>
              {t("collapse", language)}
            </Button>
          </div>

          <div style={{ flexGrow: 1, overflowY: "auto" }}>
            <List>
              {entries.map(([key, value]) => (
                <ListItem
                  key={key}
                  sx={{ flexDirection: "column", alignItems: "flex-start" }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 500, color: "text.primary" }}
                  >
                    {key}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: "pre-line" }}
                  >
                    {String(value)}
                  </Typography>
                  <Divider sx={{ width: "100%", my: 1 }} />
                </ListItem>
              ))}
            </List>
          </div>
        </div>
      ) : (
        <div
          style={{
            padding: "16px",
            height: "100%",
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "8px",
              flexGrow: 1,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, color: "text.primary", flexShrink: 0 }}
              >
                {Object.keys(latestEntry)[0]}
              </Typography>
              <div
                style={{
                  overflowY: noteExpanded ? "auto" : "hidden",
                  flexGrow: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: noteExpanded ? "block" : "-webkit-box",
                    WebkitLineClamp: noteExpanded ? "unset" : 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    whiteSpace: "pre-line",
                  }}
                >
                  {String(Object.values(latestEntry)[0])}
                </Typography>
              </div>
              {String(Object.values(latestEntry)[0]).length > 100 && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setNoteExpanded(!noteExpanded);
                  }}
                  sx={{
                    mt: 0.5,
                    p: 0.5,
                    alignSelf: "flex-start",
                    flexShrink: 0,
                  }}
                >
                  {noteExpanded ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              )}
            </div>
            <Button
              onClick={onToggleExpand}
              sx={{
                color: "primary.main",
                flexShrink: 0,
                alignSelf: "flex-start",
              }}
            >
              {t("viewAll", language)}
            </Button>
          </div>
        </div>
      )}
    </Paper>
  );
};
